import { GaugeChartProps } from '../types/chartProps';
import { gaugeDefaults } from './chartDefaults';
import { formatValue } from './formatters';
import { escapeHtml } from '@opengov/reporting-types/src/escapeHtml';
import { applyYAxisLabelsOptions } from './axisUtils';
import { applyDescriptionOptions, applyTitleOptions } from './commonChartUtils';
import { GaugeValueType, SeriesGaugeElement } from '../types';
import { AlignValue, VerticalAlignValue } from 'highcharts';

type GaugeElementObj = {
  [key in GaugeValueType]: SeriesGaugeElement;
};

export function extractGaugeElements(elements: SeriesGaugeElement[]): GaugeElementObj {
  return elements.reduce(
    (acc, element) => {
      acc[element.type] = element;
      return acc;
    },
    {
      actual: {
        type: 'actual',
        value: 0
      },
      target: {
        type: 'target',
        value: 0
      },
      min: {
        type: 'min',
        value: 0
      }
    } as GaugeElementObj
  );
}

export function renderTooltipForGaugeChart(props: GaugeChartProps) {
  return function (this: Highcharts.TooltipFormatterContextObject) {
    const formattedValue = escapeHtml(formatValue(props.formatting, this.y ?? 0, props.valueDisplayType));
    return `<div style="display: flex;"><p>${escapeHtml(this.series.name)}: </p><p><b>${formattedValue}</b></p></div>`;
  };
}

export function applyGaugeChartYAxisMinMaxOptions(options: Highcharts.Options, params: GaugeChartProps) {
  const { min, actual, target } = extractGaugeElements(params.series);
  const max = Math.max(actual.value, target.value);
  options.yAxis = {
    ...options.yAxis,
    min: min.value,
    max
  };
  return options;
}
/**
 * The color the value arc and the reading's legend swatch are both drawn with, so the legend cannot
 * end up contradicting the chart. A missing or empty configured color leaves both on the stock one.
 *
 * "Not configured" has two representations here — the key is absent until the user picks a color,
 * and clearing the picker can leave an empty string behind — so this is a blank check rather than a
 * nullish one. `??` would let `''` through and hand Highcharts an empty color; the early return says
 * so explicitly rather than leaning on `||`, which `typescript:S6606` flags without being able to
 * see why the looser test is deliberate.
 */
export function resolveGaugeValueColor(params: GaugeChartProps): string {
  const configured = params.gaugeValueColor?.trim();
  if (!configured) {
    return gaugeDefaults.color.leftBand;
  }
  return configured;
}

export function applyGaugeChartPlotBandOptions(options: Highcharts.Options, params: GaugeChartProps) {
  const { min, actual, target } = extractGaugeElements(params.series);
  const valueColor = resolveGaugeValueColor(params);
  const targetCrossed = actual.value > target.value;
  const midValue = Math.min(actual.value, target.value);
  const maxValue = Math.max(actual.value, target.value);

  const leftBand = {
    from: min.value,
    to: midValue,
    color: valueColor,
    thickness: gaugeDefaults.thickness.default
  };

  // Past the maximum there is no remaining arc left to draw, so the whole span reads as the value
  // color.
  const rightBand = {
    from: midValue,
    to: maxValue,
    color: targetCrossed ? valueColor : gaugeDefaults.color.rightBand,
    thickness: gaugeDefaults.thickness.default
  };

  const targetCrossedTick = targetCrossed
    ? {
        from: midValue,
        to: midValue,
        color: gaugeDefaults.color.targetCrossedTick,
        borderWidth: 4,
        borderColor: gaugeDefaults.color.targetCrossedTick,
        thickness: gaugeDefaults.thickness.default + gaugeDefaults.thickness.extra,
        zIndex: 3
      }
    : {};

  options.yAxis = {
    ...options.yAxis,
    plotBands: [leftBand, targetCrossedTick, rightBand]
  };

  return options;
}

export function applyGaugeChartSeriesOptions(options: Highcharts.Options, params: GaugeChartProps) {
  const { actual, target } = extractGaugeElements(params.series);

  options.series = [
    {
      type: 'gauge',
      name: actual.label ?? '',
      data: [actual.value],
      showInLegend: !params.hideLegends,
      color: resolveGaugeValueColor(params)
    },
    {
      type: 'gauge',
      name: target.label ?? '',
      showInLegend: !params.hideLegends,
      color: gaugeDefaults.color.rightBand
    }
  ];
  return options;
}

export function applyGaugeChartTooltipOptions(options: Highcharts.Options, params: GaugeChartProps) {
  options.tooltip = {
    ...options.tooltip,
    formatter: renderTooltipForGaugeChart(params)
  };
  return options;
}

export function applyGaugeChartDataLabelOptions(options: Highcharts.Options, params: GaugeChartProps) {
  const { actual, target } = extractGaugeElements(params.series);
  const remaining = target.value - actual.value;
  const formattedActual = escapeHtml(formatValue(params.formatting, actual.value, params.valueDisplayType));
  const formattedRemaining = escapeHtml(formatValue(params.formatting, Math.abs(remaining), params.valueDisplayType));
  const category = escapeHtml(params.category);

  const isTargetAchieved = remaining < 0;

  options.plotOptions = {
    ...options.plotOptions,
    series: {
      ...options.plotOptions?.series,
      dataLabels: {
        allowOverlap: true,
        enabled: true,
        formatter: function () {
          return `
          <div style="text-align:center;">
            <span style="font-size:16px; font-weight: 600; ${
              isTargetAchieved ? 'color: #992F3A;' : ''
            }">${formattedActual}</span><br/>
            <span style="font-size:12px; font-weight: 400; ${
              isTargetAchieved ? 'color: #992F3A;' : ''
            }">${formattedRemaining} ${isTargetAchieved ? 'Over' : 'Remaining'}</span><br/><br/>
            <span style="font-size:12px; font-weight: 400; margin-top: 2px;">${category}</span>
          </div>
  `;
        }
      }
    }
  };

  return options;
}

function escapeLegendName(this: Highcharts.Point | Highcharts.Series) {
  return escapeHtml(this.name);
}

export function applyGaugeChartLegendOptions(options: Highcharts.Options, params: GaugeChartProps) {
  const align = params.legend?.align as AlignValue;
  const isTopRightLeft =
    (align === 'right' || align === 'left') && (params.legend?.verticalAlign as VerticalAlignValue) === 'top';
  const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 1040;
  const yOffset = isSmallScreen ? 0 : isTopRightLeft ? 40 : 0;
  options.legend = {
    ...options.legend,
    align: params.legend?.align as AlignValue,
    verticalAlign: params.legend?.verticalAlign as VerticalAlignValue,
    y: yOffset,
    labelFormatter: escapeLegendName
  };
  return options;
}
export function applyGaugeChartOptions(options: Highcharts.Options, params: GaugeChartProps) {
  const fns = [
    applyTitleOptions,
    applyDescriptionOptions,
    applyYAxisLabelsOptions,
    applyGaugeChartYAxisMinMaxOptions,
    applyGaugeChartPlotBandOptions,
    applyGaugeChartSeriesOptions,
    applyGaugeChartTooltipOptions,
    applyGaugeChartDataLabelOptions,
    applyGaugeChartLegendOptions
  ];

  return fns.reduce((acc, fn) => fn(acc, params), options);
}
